import { Suspense, useEffect, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { PromisePool } from '@supercharge/promise-pool';
import { m, useScroll, useSpring } from 'framer-motion';
// @mui
import { useTheme } from '@mui/material/styles';

// components
import { CircularProgress, Stack, Box } from '@mui/material';
// sections
import HomeHero from '../sections/home/HomeHero';
import useLocales from '../hooks/useLocales';

import { useDispatch, useSelector } from '../redux/store';
import { getPyramid, getWidgets, getPublishedPeriods, getRootEntity } from '../redux/slices/frontend';
import { isNilOrBlank } from '../utils/core';

const LandingHugePackElements = lazy(() => import('../components/landing/LandingHugePackElements'));
const ResultsCharts = lazy(() => import('./public/kyrgyz/ResultsCharts'));
const Contents = lazy(() => import('./public/shared/contents/Contents'));

// redux

const components = {
  kyrgyz: [
    { label: 'Results', component: <ResultsCharts /> },
    { label: 'Other charts', component: <LandingHugePackElements /> },
    { label: 'CMS Contents', component: <Contents /> }
  ],
  djibouti: [{ label: 'CMS Contents', component: <Contents /> }],
  lesotho: [{ label: 'CMS Contents', component: <Contents /> }],
  comoros: [{ label: 'CMS Contents', component: <Contents /> }],
  kobikisa: [{ label: 'CMS Contents', component: <Contents /> }],
  tchad: [{ label: 'CMS Contents', component: <Contents /> }],
  liberia: [{ label: 'CMS Contents', component: <Contents /> }]
};

// ----------------------------------------------------------------------

export default function HomePage() {
  const { translate: t } = useLocales();
  const dispatch = useDispatch();
  const { currentEntityId } = useSelector((state) => state.frontend);
  const theme = useTheme();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const progress = (
    <m.div
      style={{
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 1999,
        position: 'fixed',
        transformOrigin: '0%',
        backgroundColor: theme.palette.primary.main,
        scaleX
      }}
    />
  );

  const loadHome = async () => {
    await PromisePool.for([
      dispatch(getRootEntity()),
      dispatch(getPyramid()),
      dispatch(getPublishedPeriods()),
      dispatch(getWidgets())
    ])
      .withConcurrency(4)
      .process(async (job) => job);
  };

  useEffect(() => {
    if (isNilOrBlank(currentEntityId)) {
      loadHome();
    }
  }, [currentEntityId]); // eslint-disable-line

  return (
    <>
      <Helmet>
        <title>{`${t(`shared.projectMainTitle.${process.env.REACT_APP_PROJECT_NAME}`)} - ${t(
          `shared.projectMainSubTitle.${process.env.REACT_APP_PROJECT_NAME}`
        )}`}</title>
      </Helmet>

      {progress}

      <HomeHero />

      <Box
        id="move_top"
        sx={{
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'background.default'
        }}
      >
        {/*
          <pre>
            <br />
            <br />
            <br />
            {JSON.stringify(widgets, null, 2)}
          </pre>
      */}
        {components[process.env.REACT_APP_PROJECT_NAME] !== undefined &&
          components[process.env.REACT_APP_PROJECT_NAME].map((config, configIndex) => (
            <Suspense
              key={`${config.component.label} + ${configIndex}`}
              fallback={
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <CircularProgress size={32} />
                </Stack>
              }
            >
              {config.component}
            </Suspense>
          ))}
      </Box>
    </>
  );
}
